import { useCallback } from 'react';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import {
  getWorkingProjectEventId,
  getWorkingProjectUuid,
  selectWorkingProjectDefaultLanguage,
  selectWorkingSession,
} from '@/store/slices/project/selectors';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { duplicateProject, duplicateSession } from '@/store/slices/project';
import { getActiveChannel } from '@/store/slices/user/selectors';
import useLanguageParams from '@/utils/use-language-params';

interface Props {
  mode: 'project' | 'session';
  setShowDuplicateModal: (mode: 'project' | 'session' | null) => void;
}
const DuplicateProjectButton = ({ mode = 'project', setShowDuplicateModal }: Props) => {
  const dispatch = useAppDispatch();
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const projectEventId = useAppSelector(getWorkingProjectEventId);
  const defaultLanguage = useAppSelector(selectWorkingProjectDefaultLanguage);
  const channel = useAppSelector(getActiveChannel);
  const workingSession = useAppSelector(selectWorkingSession);

  const languageParam = useLanguageParams();
  const language = languageParam || defaultLanguage || 'en';

  const handleDuplicate = useCallback(() => {
    if (mode === 'project') {
      if (projectUuid && channel) {
        dispatch(duplicateProject({ projectUuid, channel }));
        setShowDuplicateModal('project');
      }
    } else {
      if (workingSession && projectEventId && projectUuid) {
        dispatch(
          duplicateSession({
            event: projectEventId,
            projectUuid,
            session: {
              ...workingSession,
              title: {
                ...workingSession.title,
                base: workingSession.title.base + ' copy',
                [language]: workingSession.title[language] + ' copy',
              },
            },
          })
        );

        setShowDuplicateModal('session');
      }
    }
  }, [mode, projectUuid, channel, dispatch, setShowDuplicateModal, workingSession, projectEventId, language]);

  return (
    <>
      <Button
        hoverBackgroundColor="var(--gray-700)"
        outlineOnFocus={false}
        className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10"
        onClick={handleDuplicate}
      >
        <Icon name={ICONS.COPY_DEFAULT} />
        <Text color="var(--gray-100)" className="P2">
          Duplicate {mode === 'session' ? 'session' : ''}
        </Text>
      </Button>
    </>
  );
};

export default DuplicateProjectButton;
